import React from 'react'
import Styles from './FirstCreation.module.scss'
import { MultiStepStepProps } from 'components/MultiStepFlow/Types'
import SelectionStep, { SelectionOption } from 'components/FirstTimeExperienceModal/SelectionStep/SelectionStep'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import useURLParams from 'hooks/useURLParams'
import { Wizard } from 'components/Wizards/WizardsDrawer/WizardsDrawerV4/Types'
import { useWizardOpener } from 'screens/Registration/useWizardOpener'
import { shuffle } from 'lodash'
import { BrandKitShow } from 'types'
import { useOnboard } from 'hooks/queries/useOnboard'
import { useCreateFieldsetCompletionSync } from 'hooks/queries/workspaces/useCreateFieldsetCompletionSync'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Blog } from 'almanac-ui/icons/blaze/24/Blog'
import { FacebookBrand } from 'blaze-ui/icons/35'
import { InstagramBrand as InstagramBrand24 } from 'blaze-ui/icons/24'
import cx from 'classnames'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { UPLOAD_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'

export const CREATION_GOAL_ASSETS: Record<string, string> = {
  instagram:
    'https://res.cloudinary.com/almanac/image/upload/v1746778989/onboarding_assets/creation_goal/instagram_zcjbhq.png',
  blogs_seo:
    'https://res.cloudinary.com/almanac/image/upload/v1746779360/onboarding_assets/creation_goal/blog_p328ma.png',
  linkedin:
    'https://res.cloudinary.com/almanac/image/upload/v1746778989/onboarding_assets/creation_goal/linkedin_pyeil4.png',
  facebook:
    'https://res.cloudinary.com/almanac/image/upload/v1746778988/onboarding_assets/creation_goal/facebook_uhj4li.png',
  video: 'https://res.cloudinary.com/almanac/image/upload/v1740094765/onboarding_assets/creation_goal/video_zejeoc.png',
  marketplace:
    'https://res.cloudinary.com/almanac/image/upload/v1740094763/onboarding_assets/creation_goal/marketplace_qsp9w3.png',
  website:
    'https://res.cloudinary.com/almanac/image/upload/v1740094765/onboarding_assets/creation_goal/website_wjkqym.png',
  other: 'https://res.cloudinary.com/almanac/image/upload/v1740094763/onboarding_assets/creation_goal/other_khy9nt.png',
}

export type FirstCreationType =
  | 'instagram-portrait-post'
  | 'facebook-post'
  | 'linkedin-post'
  | ChannelType.BlogPost
  | ChannelType.Newsletter
  | 'other'

const FirstCreationOptionsDesktop: SelectionOption<FirstCreationType>[] = [
  {
    title: 'Instagram Post',
    value: 'instagram-portrait-post',
    image: CREATION_GOAL_ASSETS['instagram'],
    icon: <InstagramBrand24 size={32} />,
  },
  {
    title: 'Facebook Post',
    value: 'facebook-post',
    image: CREATION_GOAL_ASSETS['facebook'],
    icon: <FacebookBrand size={32} />,
  },
  {
    title: 'LinkedIn Post',
    value: 'linkedin-post',
    image: CREATION_GOAL_ASSETS['linkedin'],
    icon: <LinkedInBrand />,
  },
  {
    title: 'Blog Post',
    value: ChannelType.BlogPost,
    image: CREATION_GOAL_ASSETS['blogs_seo'],
    icon: <Blog size={32} />,
  },
]

const FirstCreationOptionsMobile: SelectionOption<FirstCreationType>[] = [
  {
    ...FirstCreationOptionsDesktop[0],
    icon: <InstagramBrand24 />,
  },
  {
    ...FirstCreationOptionsDesktop[1],
    icon: <FacebookBrand size={24} />,
  },
  {
    ...FirstCreationOptionsDesktop[2],
    icon: <LinkedInBrand size={24} />,
  },
  {
    ...FirstCreationOptionsDesktop[3],
    icon: <Blog size={24} />,
  },
]

export const FirstCreation: React.FC<MultiStepStepProps & { brandKit: BrandKitShow }> = ({
  onBack,
  onSkip,
  brandKit,
}) => {
  const { isMobile } = useResponsiveDesign()
  const renderMedia = (option: SelectionOption<FirstCreationType>) => {
    return option.image ? (
      <img
        src={option.image}
        alt={option.title}
        className={cx(Styles.image, { [Styles.blogPostImage]: option.value === ChannelType.BlogPost })}
      />
    ) : null
  }
  const { mutateAsync: onboard } = useOnboard()

  const openWizard = useWizardOpener()

  const { mutateAsync: createFieldsetCompletion, isLoading } = useCreateFieldsetCompletionSync()

  const handleNext = async (event?: any) => {
    const selectedCreationType = event?.goal || searchParams.get('firstCreation')
    const { fieldset_completion: fieldsetCompletion } = await createFieldsetCompletion({
      generationType: selectedCreationType as Wizard,
      completedFields: { brand_kit_id: brandKit.id },
      fields: ['topic'],
    })

    openWizard({
      type: selectedCreationType as Wizard,
      onSuccess: async () => {
        await onboard()
      },
      initialValues: {
        topic: fieldsetCompletion.completed_fields['topic'],
        initial_media_file_id: shuffle(
          brandKit?.media_files.filter((file) => UPLOAD_IMAGE_MIME_TYPES_NO_SVG.includes(file.mime_type)).slice(0, 4),
        )[0]?.id,
        startFromBrandKit: true,
      },
    })
  }

  const [searchParams] = useURLParams()

  return (
    <SelectionStep
      title="Your Brand Kit is ready! What do you want to create first?"
      options={isMobile ? FirstCreationOptionsMobile : FirstCreationOptionsDesktop}
      paramName="firstCreation"
      dataKey="firstCreation"
      onNext={handleNext}
      onBack={onBack}
      onSkip={onSkip}
      renderMedia={renderMedia}
      generateEvent={(value) => ({ type: 'NEXT', goal: value })}
      columnsCount={isMobile ? 2 : 4}
      variant="square"
      maxContentWidth={isMobile ? '50%' : '100%'}
      isNextButtonDisabled={isLoading}
      cardLabelSize={isMobile ? 'md' : 'sm'}
    />
  )
}

export default FirstCreation
